import { inject, Injectable } from '@angular/core';
import { Store } from '@fc-core/store-utils/store';
import type { ChatMessage } from '../models/chat-message';
import { ChatMessagesApiService } from '../services/chat-messages-api.service';
import type { AttachmentModel } from '@fc-vehicles/service/attachment-model';

export interface ChatMessagesState {
  messages: { [channel: string]: ChatMessage[] };
  previousAvailable: { [channel: string]: boolean };
  nextAvailable: { [channel: string]: boolean };
  messagesError: string | null;
  initialMessagesLoading: { [channel: string]: boolean };
  initialMessagesLoaded: { [channel: string]: boolean };
  initialMessagesFailed: { [channel: string]: boolean };
  olderMessagesLoading: boolean;
  newMessagesLoading: boolean;
  limit: number;
  currentChannelId: string | null;
  editMessageId: string | null;
  replyMessageId: string | null;
}

const initialState: ChatMessagesState = {
  messages: {},
  previousAvailable: {},
  nextAvailable: {},
  messagesError: null,
  initialMessagesLoading: {},
  initialMessagesLoaded: {},
  initialMessagesFailed: {},
  olderMessagesLoading: false,
  newMessagesLoading: false,
  limit: 25,
  currentChannelId: null,
  editMessageId: null,
  replyMessageId: null,
};

@Injectable({
  providedIn: 'root',
})
export class ChatMessagesStore extends Store<ChatMessagesState> {
  api = inject(ChatMessagesApiService);

  constructor() {
    super(initialState);
  }

  setEditMessageId(messageId: string | null) {
    this.update({ editMessageId: messageId });
  }

  setReplyMessageId(messageId: string | null) {
    this.update({ replyMessageId: messageId });
  }

  setCurrentChannelId(channelId: string) {
    this.update({ currentChannelId: channelId });
  }

  async getInitialMessages(
    channelId: string,
  ): Promise<ChatMessage[] | undefined> {
    const cachedMessages = this.selectors.messages()[channelId];
    if (cachedMessages?.length) {
      return cachedMessages;
    }

    this.draftUpdate((state) => {
      state.initialMessagesLoading[channelId] = true;
      state.initialMessagesFailed[channelId] = false;
      state.olderMessagesLoading = false;
      state.newMessagesLoading = false;
      state.currentChannelId = channelId;
    });

    try {
      const response = await this.api.getInitialMessages(
        channelId,
        this.selectors.limit(),
      );

      this.draftUpdate((state) => {
        state.messages[channelId] = response.results;
        state.previousAvailable[channelId] = response.previous !== null;
        state.nextAvailable[channelId] = response.next !== null;
        state.initialMessagesLoading[channelId] = false;
        state.initialMessagesFailed[channelId] = false;
        state.olderMessagesLoading = false;
        state.newMessagesLoading = false;
        state.initialMessagesLoaded[channelId] = true;
      });

      return response.results;
    } catch (error) {
      this.draftUpdate((state) => {
        state.initialMessagesLoading[channelId] = false;
        state.initialMessagesFailed[channelId] = true;
      });
      this.errorState(String(error));
    }
  }

  async getOlderMessages(): Promise<ChatMessage[] | undefined> {
    const channelId = this.selectors.currentChannelId();
    if (!channelId || this.selectors.nextAvailable()[channelId] === false) {
      return;
    }
    const latestOlderMessage = this.selectors.messages()[channelId]?.[0];
    if (!latestOlderMessage) return;

    this.draftUpdate((state) => {
      state.olderMessagesLoading = true;
      state.newMessagesLoading = false;
    });

    const response = await this.api.getMessageByCursor(
      latestOlderMessage,
      this.selectors.limit(),
    );

    this.draftUpdate((state) => {
      state.previousAvailable[channelId] = response.previous !== null;
      state.nextAvailable[channelId] = response.next !== null;
      const messages = [...response.results, ...state.messages[channelId]];

      state.messages[channelId] = messages;
      state.olderMessagesLoading = false;
      state.newMessagesLoading = false;
    });

    return response.results;
  }

  async getNewerMessages(): Promise<ChatMessage[] | undefined> {
    const channelId = this.selectors.currentChannelId();
    if (!channelId || this.selectors.previousAvailable()[channelId] === false) {
      return;
    }
    const newestMessage = this.selectors.messages()[channelId]?.at(-1);
    if (!newestMessage) return;

    this.draftUpdate((state) => {
      state.olderMessagesLoading = false;
      state.newMessagesLoading = true;
    });

    const response = await this.api.getMessageByCursor(
      newestMessage,
      this.selectors.limit(),
      true,
    );

    this.draftUpdate((state) => {
      const messages = [...state.messages[channelId], ...response.results];
      state.messages[channelId] = messages;
      state.previousAvailable[channelId] = response.previous !== null;
      state.nextAvailable[channelId] = response.next !== null;
      state.olderMessagesLoading = false;
      state.newMessagesLoading = false;
    });

    return response.results;
  }

  addMessage(message: ChatMessage, channelId: string) {
    this.draftUpdate((state) => {
      const messages = state.messages[channelId];
      if (!messages) return;
      const index = messages.findIndex((msg) => msg.id === message.id);
      if (index === -1) {
        messages.push(message);
      } else {
        messages[index] = message;
      }
    });
  }

  updateMessage(message: ChatMessage, channelId: string) {
    this.draftUpdate((state) => {
      if (!state.messages[channelId]) return;
      const index = state.messages[channelId].findIndex(
        (msg) => msg.id === message.id,
      );

      if (index !== -1) {
        state.messages[channelId][index] = message;
      }
    });
  }

  updateMessageAttachment(attachment: AttachmentModel, channelId: string) {
    this.draftUpdate((state) => {
      if (!state.messages[channelId]) return;
      const messageIndex = state.messages[channelId].findIndex((msg) =>
        (msg.content.attachments ?? []).some((a) => a.id === attachment.id),
      );

      if (messageIndex !== -1) {
        const attachments =
          state.messages[channelId][messageIndex].content.attachments;
        if (!attachments) return;
        const existingAttachmentIndex = attachments.findIndex(
          (a) => a.id === attachment.id,
        );

        if (existingAttachmentIndex !== -1) {
          attachments[existingAttachmentIndex] = attachment;
        } else {
          attachments.push(attachment);
        }
      }
    });
  }

  deleteMessage(messageId: string, channelId: string) {
    this.draftUpdate((state) => {
      const messages = state.messages[channelId];
      if (!messages) return;

      const index = messages.findIndex((msg) => msg.id === messageId);
      if (index !== -1) {
        messages.splice(index, 1);
      }
    });
  }

  deleteMessages(channelId: string) {
    this.draftUpdate((state) => (state.messages[channelId] = []));
  }

  errorState(error: string) {
    this.draftUpdate((state) => (state.messagesError = error));
  }

  reset() {
    this.draftUpdate((state) => {
      state.olderMessagesLoading = false;
      state.newMessagesLoading = false;
      state.messagesError = null;
    });
  }

  clearChannelMessages(channelId: string) {
    this.draftUpdate((state) => {
      state.messages[channelId] = [];
      state.previousAvailable[channelId] = false;
      state.nextAvailable[channelId] = false;
      state.initialMessagesLoading[channelId] = false;
      state.initialMessagesLoaded[channelId] = false;
      state.initialMessagesFailed[channelId] = false;
    });
  }

  async refreshMessagesOnReconnect(
    channelId: string,
  ): Promise<ChatMessage[] | undefined> {
    if (!channelId) return;

    // Reset the loaded/failed state to allow fetching
    this.draftUpdate((state) => {
      state.initialMessagesLoaded[channelId] = false;
      state.initialMessagesFailed[channelId] = false;
      state.messages[channelId] = [];
    });

    return this.getInitialMessages(channelId);
  }

  clearFailedState(channelId: string) {
    this.draftUpdate((state) => {
      state.initialMessagesFailed[channelId] = false;
    });
  }

  // Reconcile cached messages after a WS reconnect: drop caches of inactive
  // channels (they may have silent gaps) and gap-fetch the active one.
  async syncOnReconnect(activeChannelId: string | null): Promise<void> {
    this.invalidateInactiveChannels(activeChannelId);
    if (!activeChannelId) return;

    if (this.selectors.initialMessagesFailed()[activeChannelId]) {
      this.clearFailedState(activeChannelId);
      return;
    }
    if (!this.selectors.initialMessagesLoaded()[activeChannelId]) return;

    await this.fetchMissedMessages(activeChannelId);
  }

  private invalidateInactiveChannels(activeChannelId: string | null): void {
    const channelIds = Object.keys(this.selectors.messages());
    for (const channelId of channelIds) {
      if (channelId !== activeChannelId) {
        this.clearChannelMessages(channelId);
      }
    }
  }

  private async fetchMissedMessages(channelId: string): Promise<void> {
    const messages = this.selectors.messages()[channelId];
    const newestMessage = messages?.[messages.length - 1];
    if (!newestMessage) {
      await this.refreshMessagesOnReconnect(channelId);
      return;
    }

    try {
      const response = await this.api.getMessageByCursor(
        newestMessage,
        this.selectors.limit(),
        true,
      );

      if (response.previous !== null) {
        // Gap is larger than one page, a full reload is cheaper than paging
        await this.refreshMessagesOnReconnect(channelId);
        return;
      }

      this.draftUpdate((state) => {
        const existing = state.messages[channelId];
        if (!existing) return;
        const knownIds = new Set(existing.map((msg) => msg.id));
        for (const message of response.results) {
          if (!knownIds.has(message.id)) {
            existing.push(message);
          }
        }
        state.previousAvailable[channelId] = false;
      });
    } catch (_) {
      await this.refreshMessagesOnReconnect(channelId);
    }
  }
}

export function getCursor(url: string) {
  const urlObj = new URL(url);
  const cursor = urlObj.searchParams.get('cursor');
  return cursor;
}
